import { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ShowChart,
  AutoGraph,
  Psychology,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { StrategyPerformance, StrategySignal, Trade } from '../types';
import { fetchStrategies, fetchStrategySignals, fetchTrades } from '../services/api';

const COLORS = ['#6366f1', '#22d3ee', '#10b981', '#f59e0b', '#ef4444', '#a855f7'];

function StrategyRow({ s, index }: { s: StrategyPerformance; index: number }) {
  return (
    <TableRow sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS[index % COLORS.length] }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.strategyName}</Typography>
            <Typography variant="caption" color="text.secondary">{s.type}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={s.isActive ? 'ACTIVE' : 'INACTIVE'}
          size="small"
          color={s.isActive ? 'success' : 'default'}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
        {s.totalTrades}
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: s.winRate >= 0.5 ? 'success.main' : 'error.main' }}>
        {(s.winRate * 100).toFixed(1)}%
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: s.profitFactor >= 1 ? 'success.main' : 'error.main' }}>
        {s.profitFactor.toFixed(2)}
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
        {s.Sharpe.toFixed(2)}
      </TableCell>
      <TableCell align="right">
        <LinearProgress
          variant="determinate"
          value={Math.min(s.maxDrawdown * 100, 100)}
          sx={{
            width: 60, display: 'inline-flex', height: 6, borderRadius: 3, mr: 1,
            '& .MuiLinearProgress-bar': { backgroundColor: s.maxDrawdown > 0.15 ? '#ef4444' : s.maxDrawdown > 0.08 ? '#f59e0b' : '#10b981' },
          }}
        />
        <Typography component="span" variant="caption" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
          {(s.maxDrawdown * 100).toFixed(1)}%
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: s.totalPnl >= 0 ? 'success.main' : 'error.main' }}>
        {s.totalPnl >= 0 ? '+' : ''}{s.totalPnl.toFixed(2)}
      </TableCell>
    </TableRow>
  );
}

interface Props { onNotify: (msg: string) => void; }

export default function StrategyPanel({}: Props) {
  const [strategies, setStrategies] = useState<StrategyPerformance[]>([]);
  const [signals, setSignals] = useState<StrategySignal[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [view, setView] = useState<'overview' | 'signals' | 'history'>('overview');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [strats, sigs, trds] = await Promise.all([
        fetchStrategies().catch(() => []),
        fetchStrategySignals().catch(() => []),
        fetchTrades(20).catch(() => []),
      ]);
      setStrategies(strats);
      setSignals(sigs);
      setTrades(trds);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && strategies.length === 0) return <LinearProgress sx={{ mb: 2 }} />;

  // Build comparison chart data
  const chartData = strategies.map((s) => ({
    name: s.strategyName.length > 12 ? s.strategyName.slice(0, 12) + '…' : s.strategyName,
    PnL: s.totalPnl,
    WinRate: s.winRate * 100,
    Sharpe: s.Sharpe,
    MaxDD: s.maxDrawdown * 100,
  }));

  return (
    <>
      {/* ── Strategy Comparison Chart ── */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoGraph sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Strategy Comparison
                </Typography>
              </Box>
              <ToggleButtonGroup
                size="small"
                value={view}
                exclusive
                onChange={(_, v) => v && setView(v)}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 } }}
              >
                <ToggleButton value="overview">Overview</ToggleButton>
                <ToggleButton value="signals">Signals</ToggleButton>
                <ToggleButton value="history">Trade History</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {view === 'overview' && (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={4}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8b949e' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#8b949e' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#8b949e' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid #30363d', borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number, name: string) => {
                      if (name === 'PnL') return [`$${v.toFixed(2)}`, 'PnL'];
                      if (name === 'MaxDD') return [`${v.toFixed(1)}%`, 'Max DD'];
                      return [v.toFixed(2), name];
                    }}
                  />
                  <Bar yAxisId="left" dataKey="PnL" name="PnL" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                  <Bar yAxisId="right" dataKey="MaxDD" name="MaxDD" fill="#30363d" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}

            {view === 'signals' && (
              <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
                {signals.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No recent signals
                  </Typography>
                )}
                {signals.slice(0, 20).map((sig) => (
                  <Box key={sig.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1, backgroundColor: '#0f1117' }}>
                    <Chip
                      label={sig.signalType.toUpperCase()}
                      size="small"
                      color={sig.signalType === 'buy' ? 'success' : sig.signalType === 'sell' ? 'error' : 'default'}
                      sx={{ fontSize: '0.65rem', height: 20, minWidth: 40 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {sig.symbol} — {sig.strategyName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{sig.reason.slice(0, 80)}{sig.reason.length > 80 ? '…' : ''}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem' }}>
                        {new Date(sig.createdAt).toLocaleTimeString()}
                      </Typography>
                      <LinearProgress variant="determinate" value={sig.confidence * 100} sx={{ width: 50, height: 3, borderRadius: 2, mt: 0.5, ml: 'auto', '& .MuiLinearProgress-bar': { backgroundColor: '#6366f1' } }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}

            {view === 'history' && (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Side</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Exec Price</TableCell>
                      <TableCell align="right">P&L</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Strategy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="caption" color="text.secondary">No trade history</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {trades.map((t) => (
                      <TableRow key={t.id} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {t.executedAt ? new Date(t.executedAt).toLocaleString() : new Date(t.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={t.symbol} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', height: 20 }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={t.side.toUpperCase()} size="small" color={t.side === 'buy' ? 'success' : 'error'} sx={{ fontSize: '0.65rem', height: 20 }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                          {t.executedQuantity?.toFixed(4) ?? t.quantity.toFixed(4)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                          ${t.executedPrice?.toFixed(2) ?? '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: (t.pnl ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                          {t.pnl !== null ? `${t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={t.status.toUpperCase()} size="small" color={t.status === 'filled' ? 'success' : t.status === 'rejected' ? 'error' : 'default'} sx={{ fontSize: '0.6rem', height: 18 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{t.strategyName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* ── Strategy Performance Table ── */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChart sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Strategy Performance ({strategies.length})
                </Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Strategy</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Trades</TableCell>
                    <TableCell align="right">Win Rate</TableCell>
                    <TableCell align="right">Profit Factor</TableCell>
                    <TableCell align="right">Sharpe</TableCell>
                    <TableCell align="right">Max DD</TableCell>
                    <TableCell align="right">Total PnL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {strategies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="caption" color="text.secondary" sx={{ py: 2, display: 'block' }}>
                          No strategies registered
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {strategies.map((s, i) => (
                    <StrategyRow key={s.strategyId} s={s} index={i} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* ── ML Predictions ── */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Psychology sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                AI Predictions
              </Typography>
            </Box>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, backgroundColor: '#0f1117' }}>
                <Box>
                  <Typography variant="body2">BTC/USDT — 4h</Typography>
                  <Chip label="UP" size="small" color="success" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                    74.2%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">confidence</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, backgroundColor: '#0f1117' }}>
                <Box>
                  <Typography variant="body2">ETH/USDT — 4h</Typography>
                  <Chip label="UP" size="small" color="success" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                    68.9%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">confidence</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, backgroundColor: '#0f1117' }}>
                <Box>
                  <Typography variant="body2">SUI/USDT — 1h</Typography>
                  <Chip label="HOLD" size="small" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5, backgroundColor: '#30363d' }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                    52.1%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">confidence</Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* ── Model Accuracy ── */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Psychology sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Model Accuracy Tracker
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              {[
                { name: 'LightGBM-BTC', version: 'v2.3.1', accuracy: 0.642, trades: 342 },
                { name: 'XGBoost-ETH', version: 'v1.8.0', accuracy: 0.618, trades: 289 },
                { name: 'Ensemble-Main', version: 'v3.1.0', accuracy: 0.671, trades: 512 },
              ].map((m) => (
                <Box key={m.name}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{m.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{m.version} · {m.trades} predictions</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: m.accuracy >= 0.6 ? 'success.main' : 'warning.main' }}>
                      {(m.accuracy * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={m.accuracy * 100}
                    sx={{
                      height: 4, borderRadius: 2,
                      '& .MuiLinearProgress-bar': { backgroundColor: m.accuracy >= 0.65 ? '#10b981' : m.accuracy >= 0.55 ? '#f59e0b' : '#ef4444' },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}
